import { useState, useRef, useCallback, useEffect } from 'react';
import { api } from '../utils/api.js';

/* ─── Design system injection ─── */
const SETTINGS_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');
  .settings-root * { font-family: 'DM Sans', sans-serif; }
  .settings-section-title { font-family: 'Syne', sans-serif !important; }
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(8px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes overlayIn { from { opacity:0; } to { opacity:1; } }
  @keyframes modalLift { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
  @keyframes cardIn { from { opacity:0; transform:translateY(6px) scale(.99); } to { opacity:1; transform:translateY(0) scale(1); } }

  /* Mobile nav grid — 4 tabs after removing Account & Risk */
  .settings-mobile-nav {
    display: grid !important;
    grid-template-columns: repeat(4, 1fr) !important;
    border-bottom: 1px solid rgba(255,255,255,.06);
    margin-bottom: 20px;
    width: 100%;
    box-sizing: border-box;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .settings-mobile-nav button {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 4px;
    padding: 10px 4px 12px;
    background: transparent !important;
    border: none !important;
    cursor: pointer;
    transition: all .15s;
    position: relative;
    min-height: 56px;
    min-width: 0;
    -webkit-appearance: none;
  }

  /* Account card hover lift */
  .acct-card {
    transition: border-color .2s, background .2s, box-shadow .2s, transform .2s;
  }
  .acct-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(0,0,0,.3);
  }
  .acct-card.acct-card--active {
    box-shadow: 0 0 0 1px rgba(212,168,67,.25), 0 8px 24px rgba(212,168,67,.08);
  }

  /* Stat cell — never wrap */
  .acct-stat-cell { min-width: 0; overflow: hidden; }
  .acct-stat-value {
    font-family: 'Syne', sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Action buttons */
  .acct-action-btn { transition: border-color .15s, color .15s, background .15s, transform .1s; }
  .acct-action-btn:hover { transform: translateY(-1px); }

  /* Scrollbar */
  .settings-scroll::-webkit-scrollbar { width: 4px; }
  .settings-scroll::-webkit-scrollbar-track { background: transparent; }
  .settings-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,.08); border-radius: 2px; }

  /* Mobile-specific fixes */
  @media (max-width: 640px) {
    .settings-root { overflow-x: hidden !important; }
    .acct-card { min-width: 0; }
    .settings-accounts-grid { grid-template-columns: 1fr !important; }
  }
`;


/* ─── Full IANA timezone list ─── */
const TIMEZONES = [
  { group: 'Africa',         zones: ['Africa/Abidjan','Africa/Accra','Africa/Addis_Ababa','Africa/Cairo','Africa/Casablanca','Africa/Johannesburg','Africa/Kigali','Africa/Lagos','Africa/Nairobi','Africa/Tunis'] },
  { group: 'America',        zones: ['America/Anchorage','America/Bogota','America/Buenos_Aires','America/Chicago','America/Denver','America/Los_Angeles','America/Mexico_City','America/New_York','America/Sao_Paulo','America/Toronto','America/Vancouver'] },
  { group: 'Asia',           zones: ['Asia/Almaty','Asia/Bangkok','Asia/Colombo','Asia/Dubai','Asia/Hong_Kong','Asia/Jakarta','Asia/Karachi','Asia/Kolkata','Asia/Kuala_Lumpur','Asia/Riyadh','Asia/Seoul','Asia/Shanghai','Asia/Singapore','Asia/Taipei','Asia/Tehran','Asia/Tokyo'] },
  { group: 'Atlantic/Indian',zones: ['Atlantic/Reykjavik','Indian/Mauritius','Indian/Nairobi'] },
  { group: 'Australia',      zones: ['Australia/Adelaide','Australia/Brisbane','Australia/Melbourne','Australia/Perth','Australia/Sydney'] },
  { group: 'Europe',         zones: ['Europe/Amsterdam','Europe/Athens','Europe/Berlin','Europe/Brussels','Europe/Budapest','Europe/Copenhagen','Europe/Dublin','Europe/Helsinki','Europe/Istanbul','Europe/Kiev','Europe/Lisbon','Europe/London','Europe/Madrid','Europe/Moscow','Europe/Oslo','Europe/Paris','Europe/Prague','Europe/Rome','Europe/Stockholm','Europe/Vienna','Europe/Warsaw','Europe/Zurich'] },
  { group: 'Pacific',        zones: ['Pacific/Auckland','Pacific/Fiji','Pacific/Guam','Pacific/Honolulu','Pacific/Noumea','Pacific/Sydney'] },
  { group: 'UTC',            zones: ['UTC'] },
];

const LEVELS = ['Level 1 Student','Level 2 Student','Level 3 Student','Funded Trader','Professional'];

const ACCOUNT_TYPES = ['Live', 'Demo', 'Prop Firm', 'Paper'];

const PROP_FIRMS = ['FTMO', 'MyForexFunds', 'The5%ers', 'FundedNext', 'Topstep', 'Apex Trader Funding', 'Other'];

const ACCOUNT_TYPE_META = {
  Live:       { color: 'var(--green)',  bg: 'rgba(46,204,138,.1)',   border: 'rgba(46,204,138,.25)',  icon: '📈' },
  Demo:       { color: 'var(--blue)',   bg: 'rgba(91,143,232,.1)',   border: 'rgba(91,143,232,.25)',  icon: '🧪' },
  'Prop Firm':{ color: 'var(--gold)',   bg: 'rgba(212,168,67,.1)',   border: 'rgba(212,168,67,.25)',  icon: '🏆' },
  Paper:      { color: 'rgba(200,192,168,.6)', bg: 'rgba(200,192,168,.06)', border: 'rgba(200,192,168,.15)', icon: '📄' },
};

// ─── Nav sections: "My Accounts" removed — managed via sidebar switcher ───────
const NAV_SECTIONS = [
  { id: 'profile',  label: 'Profile',        icon: '👤' },
  { id: 'rules',    label: 'My Rules',        icon: '📋' },
  { id: 'security', label: 'Security',        icon: '🔒' },
  { id: 'privacy',  label: 'Data & Privacy',  icon: '🛡️' },
];

/* ─── Responsive hook ─── */
function useWindowWidth() {
  const [w, setW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return w;
}

/* ─── Helpers ─── */
function getInitials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function validateTradingAccount({ name, balance }) {
  const errors = {};
  if (!name || !name.trim()) errors.name = 'Account name is required.';
  if (balance !== '' && (isNaN(parseFloat(balance)) || parseFloat(balance) < 0)) errors.balance = 'Must be a positive number.';
  return errors;
}

function generateId() {
  return Math.random().toString(36).slice(2, 10);
}

/* ════════════════════════════════════
   Shared sub-components
════════════════════════════════════ */

function TInput({ value, onChange, placeholder, type = 'text', prefix, suffix, readOnly, autoFocus, error }) {
  const [f, setF] = useState(false);
  const borderColor = error
    ? 'rgba(224,85,85,.6)'
    : f ? 'rgba(212,168,67,.5)'
    : readOnly ? 'rgba(255,255,255,.05)'
    : 'rgba(255,255,255,.1)';
  return (
    <div>
      <div style={{ position: 'relative' }}>
        {prefix && <span style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'rgba(200,192,168,.4)',fontSize:13,pointerEvents:'none',zIndex:1 }}>{prefix}</span>}
        <input
          type={type} value={value} onChange={onChange} placeholder={placeholder}
          readOnly={readOnly} autoFocus={autoFocus}
          onFocus={() => !readOnly && setF(true)} onBlur={() => setF(false)}
          style={{
            width: '100%',
            padding: `12px ${suffix ? '36px' : '12px'} 12px ${prefix ? '28px' : '12px'}`,
            background: readOnly ? 'var(--dark4)' : 'var(--dark3)',
            border: `1px solid ${borderColor}`,
            color: readOnly ? 'rgba(240,234,214,.35)' : 'var(--white)',
            fontSize: 14, outline: 'none', borderRadius: 'var(--radius)', transition: 'border-color .15s',
            minHeight: 44,
          }}
        />
        {suffix && <span style={{ position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',color:'rgba(200,192,168,.4)',fontSize:13,pointerEvents:'none' }}>{suffix}</span>}
      </div>
      {error && <div style={{ fontSize:11,color:'var(--red)',marginTop:4 }}>{error}</div>}
    </div>
  );
}

function FieldLabel({ children }) {
  return <label style={{ display:'block',fontSize:11,fontWeight:600,color:'rgba(240,234,214,.6)',marginBottom:6,letterSpacing:'.02em' }}>{children}</label>;
}

function Toast({ msg, type = 'success' }) {
  const color = type === 'success' ? 'var(--green)' : 'var(--red)';
  const bg    = type === 'success' ? 'rgba(46,204,138,.12)' : 'rgba(224,85,85,.12)';
  const bdr   = type === 'success' ? 'rgba(46,204,138,.3)'  : 'rgba(224,85,85,.3)';
  return (
    <div style={{
      position:'fixed', bottom:20, right:16, left:16, zIndex:9999,
      padding:'12px 16px', background:bg, border:`1px solid ${bdr}`,
      color, fontSize:13, fontWeight:600,
      display:'flex', alignItems:'center', gap:8,
      animation:'fadeUp .25s ease', boxShadow:'0 8px 24px rgba(0,0,0,.4)',
      borderRadius:'var(--radius)',
      maxWidth: 360, marginLeft: 'auto',
    }}>
      {type === 'success' ? '✓' : '✕'} {msg}
    </div>
  );
}

function ConfirmModal({ title, body, confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }) {
  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,.75)',
      backdropFilter:'blur(4px)', zIndex:10000,
      display:'flex', alignItems:'flex-end', justifyContent:'center',
      padding:0, animation:'overlayIn .15s ease',
    }}>
      <div style={{
        background:'var(--dark2)', border:'1px solid rgba(255,255,255,.1)',
        borderRadius:'var(--radius) var(--radius) 0 0',
        padding:'24px 20px 32px', width:'100%', maxWidth:480,
        animation:'modalLift .2s ease', boxShadow:'0 -8px 40px rgba(0,0,0,.6)',
      }}>
        <div style={{ width:40,height:4,borderRadius:2,background:'rgba(255,255,255,.15)',margin:'0 auto 20px' }} />
        <div style={{ fontSize:15,fontWeight:700,color:'var(--white)',marginBottom:8 }}>{title}</div>
        <div style={{ fontSize:13,color:'rgba(200,192,168,.65)',lineHeight:1.6,marginBottom:24 }}>{body}</div>
        <div style={{ display:'flex',gap:10 }}>
          <button onClick={onCancel}
            style={{ flex:1,padding:'12px',background:'transparent',border:'1px solid rgba(255,255,255,.12)',color:'rgba(240,234,214,.6)',fontSize:13,fontWeight:600,cursor:'pointer',borderRadius:'var(--radius)',minHeight:44 }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            style={{ flex:1,padding:'12px',background: danger ? 'rgba(224,85,85,.15)' : 'rgba(212,168,67,.15)',border:`1px solid ${danger ? 'rgba(224,85,85,.5)' : 'rgba(212,168,67,.5)'}`,color: danger ? 'var(--red)' : 'var(--gold)',fontSize:13,fontWeight:700,cursor:'pointer',borderRadius:'var(--radius)',minHeight:44 }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function SaveButton({ dirty, onClick, label = 'Save Changes', fullWidth = false }) {
  const active = dirty;
  return (
    <button
      onClick={active ? onClick : undefined}
      style={{
        padding: '12px 24px', fontSize: 12, fontWeight: 700,
        letterSpacing: '.04em', textTransform: 'uppercase',
        cursor: active ? 'pointer' : 'default',
        borderRadius: 'var(--radius)', border: 'none',
        transition: 'all .2s',
        background: active ? 'linear-gradient(135deg,var(--gold),var(--gold-dim))' : 'rgba(255,255,255,.05)',
        color: active ? 'var(--black)' : 'rgba(255,255,255,.2)',
        boxShadow: active ? '0 4px 16px rgba(212,168,67,.3)' : 'none',
        display: 'inline-flex', alignItems: 'center', gap: 7,
        width: fullWidth ? '100%' : 'auto',
        justifyContent: fullWidth ? 'center' : 'flex-start',
        minHeight: 44,
      }}
    >
      {active ? `● ${label}` : '✓ Saved'}
    </button>
  );
}

/* ─── Desktop: vertical left nav ─── */
function DesktopNav({ active, onChange, dirty }) {
  return (
    <div style={{ width: 192, flexShrink: 0, paddingTop: 4 }}>
      <div style={{ fontSize:10,letterSpacing:'.08em',textTransform:'uppercase',color:'rgba(200,192,168,.35)',fontWeight:700,marginBottom:8,paddingLeft:12 }}>Settings</div>
      {NAV_SECTIONS.map(s => {
        const isActive = active === s.id;
        const isDirty  = dirty[s.id];
        return (
          <div key={s.id} onClick={() => onChange(s.id)}
            style={{
              display:'flex', alignItems:'center', gap:10,
              padding:'10px 12px', cursor:'pointer', borderRadius:'var(--radius)',
              marginBottom:2,
              background: isActive ? 'rgba(212,168,67,.1)' : 'transparent',
              borderLeft: `2px solid ${isActive ? 'var(--gold)' : 'transparent'}`,
              color: isActive ? 'var(--gold)' : 'rgba(240,234,214,.55)',
              fontSize:13, fontWeight: isActive ? 600 : 400,
              transition:'all .15s', userSelect:'none',
            }}
            onMouseEnter={e => { if (!isActive) { e.currentTarget.style.color='rgba(240,234,214,.8)'; e.currentTarget.style.background='rgba(255,255,255,.03)'; }}}
            onMouseLeave={e => { if (!isActive) { e.currentTarget.style.color='rgba(240,234,214,.55)'; e.currentTarget.style.background='transparent'; }}}
          >
            <span style={{ fontSize:14 }}>{s.icon}</span>
            <span>{s.label}</span>
            {isDirty && !isActive && (
              <span style={{ marginLeft:'auto',width:6,height:6,borderRadius:'50%',background:'var(--gold)',flexShrink:0,boxShadow:'0 0 6px var(--gold)' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Mobile: scrollable icon+label tab strip ─── */
function MobileNav({ active, onChange, dirty }) {
  return (
    <div className="settings-mobile-nav">
      {NAV_SECTIONS.map(s => {
        const isActive = active === s.id;
        const isDirty  = dirty[s.id];
        return (
          <button key={s.id} onClick={() => onChange(s.id)}
            style={{
              borderBottom: `2px solid ${isActive ? 'var(--gold)' : 'transparent'}`,
              color: isActive ? 'var(--gold)' : 'rgba(240,234,214,.45)',
            }}>
            {isDirty && !isActive && (
              <span style={{
                position: 'absolute', top: 8, right: '28%',
                width: 5, height: 5, borderRadius: '50%',
                background: 'var(--gold)', boxShadow: '0 0 4px var(--gold)',
              }} />
            )}
            <span style={{ fontSize: 17, lineHeight: 1 }}>{s.icon}</span>
            <span style={{
              fontSize: 10, fontWeight: isActive ? 700 : 400,
              letterSpacing: '.02em',
              whiteSpace: 'nowrap',
              color: 'inherit',
            }}>
              {s.id === 'privacy' ? 'Privacy' : s.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}


/* ════════════════════════════════════
   Account Card (read mode)
════════════════════════════════════ */
function TradingAccountCard({ acct, isActive, onSetActive, onEdit, onDelete, isMobile }) {
  const meta = ACCOUNT_TYPE_META[acct.type] || ACCOUNT_TYPE_META['Live'];
  return (
    <div
      className={`acct-card${isActive ? ' acct-card--active' : ''}`}
      style={{
        border: isActive ? `1px solid rgba(212,168,67,.4)` : '1px solid rgba(255,255,255,.07)',
        borderRadius: 'var(--radius)',
        background: isActive ? 'rgba(212,168,67,.04)' : 'rgba(255,255,255,.015)',
        padding: '16px',
        position: 'relative',
        animation: 'cardIn .22s ease',
        overflow: 'hidden',
      }}
    >
      {/* Top glow strip for active */}
      {isActive && (
        <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg,transparent,rgba(212,168,67,.6),transparent)' }} />
      )}

      {/* ACTIVE badge */}
      {isActive && (
        <div style={{
          position: 'absolute', top: 0, right: 14,
          fontSize: 8, fontWeight: 800, letterSpacing: '.08em',
          background: 'var(--gold)', color: 'var(--black)',
          padding: '3px 9px', borderRadius: '0 0 6px 6px',
          textTransform: 'uppercase', fontFamily: "'DM Sans',sans-serif",
        }}>ACTIVE</div>
      )}

      {/* Header row */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:14 }}>
        <div style={{
          width:40, height:40, flexShrink:0,
          background: meta.bg, border: `1px solid ${meta.border}`,
          borderRadius: 10,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:18,
          boxShadow: isActive ? `0 4px 12px ${meta.bg}` : 'none',
        }}>
          {meta.icon}
        </div>

        <div style={{ flex:1, minWidth:0, paddingTop:2 }}>
          <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap', marginBottom:3 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'var(--white)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:"'Syne',sans-serif" }}>
              {acct.name}
            </div>
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: '.07em',
              color: meta.color, background: meta.bg,
              border: `1px solid ${meta.border}`,
              padding: '2px 7px', borderRadius: 4, flexShrink: 0,
              textTransform: 'uppercase',
            }}>{acct.type}</span>
          </div>
          <div style={{ fontSize:11, color:'rgba(200,192,168,.4)' }}>
            {acct.broker ? acct.broker : 'No broker'}
            {acct.propFirm ? ` · ${acct.propFirm}` : ''}
          </div>
        </div>
      </div>

      {/* Stats row — always 3 cols */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6, marginBottom:14 }}>
        {[
          { label: 'Balance',     value: acct.balance   ? `$${parseFloat(acct.balance).toLocaleString()}` : '—', highlight: !!acct.balance },
          { label: 'Daily Limit', value: acct.dailyPct  ? `${acct.dailyPct}%`  : '—', highlight: false },
          { label: 'Max Trades',  value: acct.maxTrades || '—',                         highlight: false },
        ].map(stat => (
          <div key={stat.label} className="acct-stat-cell" style={{
            background: stat.highlight ? 'rgba(212,168,67,.05)' : 'rgba(255,255,255,.025)',
            border: `1px solid ${stat.highlight ? 'rgba(212,168,67,.12)' : 'rgba(255,255,255,.05)'}`,
            borderRadius: 8, padding: '9px 8px',
          }}>
            <div style={{ fontSize: 9, color:'rgba(200,192,168,.4)', marginBottom:4, fontWeight:700, letterSpacing:'.04em', textTransform:'uppercase' }}>{stat.label}</div>
            <div className="acct-stat-value" style={{ color: stat.highlight ? 'var(--gold)' : 'rgba(200,192,168,.75)' }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* Notes preview */}
      {acct.notes && (
        <div style={{ fontSize:11,color:'rgba(200,192,168,.35)',marginBottom:12,lineHeight:1.5,borderLeft:'2px solid rgba(255,255,255,.07)',paddingLeft:8,fontStyle:'italic' }}>
          {acct.notes.length > 80 ? acct.notes.slice(0,80)+'…' : acct.notes}
        </div>
      )}

      {/* Actions */}
      <div style={{ display:'flex', gap:7 }}>
        {!isActive && (
          <button onClick={() => onSetActive(acct.id)}
            className="acct-action-btn"
            style={{
              flex:1, padding:'8px 10px', fontSize:11, fontWeight:700,
              background:'linear-gradient(135deg,var(--gold),var(--gold-dim))',
              border:'none', color:'var(--black)', cursor:'pointer',
              borderRadius: 8, letterSpacing:'.04em', fontFamily:"'DM Sans',sans-serif",
              minHeight:36, display:'flex', alignItems:'center', justifyContent:'center', gap:5,
            }}>
            <span style={{ fontSize:10 }}>⚡</span> Set Active
          </button>
        )}
        {isActive && (
          <div style={{
            flex:1, padding:'8px 10px', fontSize:11, fontWeight:700,
            background:'rgba(212,168,67,.08)', border:'1px solid rgba(212,168,67,.2)',
            color:'var(--gold)', borderRadius:8, fontFamily:"'DM Sans',sans-serif",
            minHeight:36, display:'flex', alignItems:'center', justifyContent:'center', gap:5,
            letterSpacing:'.03em',
          }}>
            <span>✓</span> Currently Active
          </div>
        )}
        <button onClick={() => onEdit(acct)}
          className="acct-action-btn"
          style={{
            padding:'8px 14px', fontSize:11, fontWeight:600,
            background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.1)',
            color:'rgba(240,234,214,.65)', cursor:'pointer',
            borderRadius:8, fontFamily:"'DM Sans',sans-serif",
            minHeight:36, whiteSpace:'nowrap',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(212,168,67,.35)'; e.currentTarget.style.color='var(--gold)'; e.currentTarget.style.background='rgba(212,168,67,.06)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.1)'; e.currentTarget.style.color='rgba(240,234,214,.65)'; e.currentTarget.style.background='rgba(255,255,255,.04)'; }}>
          ✏ Edit
        </button>
        <button onClick={() => onDelete(acct.id)}
          className="acct-action-btn"
          style={{
            padding:'8px 10px', fontSize:13, fontWeight:600,
            background:'transparent', border:'1px solid rgba(224,85,85,.15)',
            color:'rgba(224,85,85,.4)', cursor:'pointer',
            borderRadius:8,
            minHeight:36,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(224,85,85,.45)'; e.currentTarget.style.color='var(--red)'; e.currentTarget.style.background='rgba(224,85,85,.06)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(224,85,85,.15)'; e.currentTarget.style.color='rgba(224,85,85,.4)'; e.currentTarget.style.background='transparent'; }}>
          ✕
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════
   Add / Edit Account Modal (bottom sheet)
════════════════════════════════════ */
function AccountFormModal({ acct, onSave, onCancel, isMobile }) {
  const isEdit = !!acct?.id;
  const [form, setForm] = useState({
    name:      acct?.name      || '',
    type:      acct?.type      || 'Live',
    broker:    acct?.broker    || '',
    propFirm:  acct?.propFirm  || '',
    balance:   acct?.balance   || '',
    dailyPct:  acct?.dailyPct  || '',
    weekPct:   acct?.weekPct   || '',
    maxTrades: acct?.maxTrades || '',
    notes:     acct?.notes     || '',
  });
  const [errors, setErrors] = useState({});
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSave = () => {
    const errs = validateTradingAccount(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    onSave({ ...form, id: acct?.id || generateId() });
  };

  const balanceNum = parseFloat(form.balance) || 0;
  const meta = ACCOUNT_TYPE_META[form.type] || ACCOUNT_TYPE_META['Live'];

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,.8)',
      backdropFilter:'blur(4px)', zIndex:10000,
      display:'flex', alignItems:'flex-end', justifyContent:'center',
      animation:'overlayIn .15s ease',
    }}>
      <div style={{
        background:'var(--dark2)', border:'1px solid rgba(255,255,255,.1)',
        borderRadius:'var(--radius) var(--radius) 0 0',
        width:'100%', maxWidth:560,
        maxHeight:'90vh', overflowY:'auto',
        animation:'modalLift .2s ease', boxShadow:'0 -8px 40px rgba(0,0,0,.6)',
      }}>
        {/* Drag handle */}
        <div style={{ padding:'16px 20px 0', position:'sticky', top:0, background:'var(--dark2)', zIndex:1 }}>
          <div style={{ width:40,height:4,borderRadius:2,background:'rgba(255,255,255,.15)',margin:'0 auto 16px' }} />
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
            <div style={{ fontSize:15, fontWeight:700, color:'var(--white)' }}>
              {isEdit ? 'Edit Account' : 'Add Trading Account'}
            </div>
            <button onClick={onCancel}
              style={{ background:'none', border:'none', color:'rgba(200,192,168,.5)', fontSize:20, cursor:'pointer', lineHeight:1, padding:'0 2px' }}>×</button>
          </div>
        </div>

        <div style={{ padding:'0 20px 32px' }}>
          {/* Account type selector */}
          <div style={{ marginBottom:16 }}>
            <FieldLabel>Account Type</FieldLabel>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
              {ACCOUNT_TYPES.map(t => {
                const m = ACCOUNT_TYPE_META[t];
                const sel = form.type === t;
                return (
                  <button key={t} onClick={() => setForm(p => ({ ...p, type: t, propFirm: t !== 'Prop Firm' ? '' : p.propFirm }))}
                    style={{
                      padding:'10px 4px', textAlign:'center',
                      background: sel ? m.bg : 'var(--dark3)',
                      border: `1px solid ${sel ? m.border : 'rgba(255,255,255,.08)'}`,
                      color: sel ? m.color : 'rgba(200,192,168,.5)',
                      fontSize:11, fontWeight: sel ? 700 : 500,
                      cursor:'pointer', borderRadius:'var(--radius)',
                      transition:'all .15s',
                    }}>
                    <div style={{ fontSize:16, marginBottom:4 }}>{m.icon}</div>
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Name + broker */}
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <FieldLabel>Account Name *</FieldLabel>
              <TInput value={form.name} onChange={set('name')} placeholder="e.g. FTMO Challenge" error={errors.name} />
            </div>
            <div>
              <FieldLabel>Broker</FieldLabel>
              <TInput value={form.broker} onChange={set('broker')} placeholder="e.g. IC Markets" />
            </div>
          </div>

          {/* Prop firm dropdown */}
          {form.type === 'Prop Firm' && (
            <div style={{ marginBottom:12 }}>
              <FieldLabel>Prop Firm</FieldLabel>
              <select value={form.propFirm} onChange={set('propFirm')}
                style={{ width:'100%',padding:'12px',background:'var(--dark3)',border:'1px solid rgba(255,255,255,.1)',color:'var(--white)',fontSize:14,outline:'none',borderRadius:'var(--radius)',appearance:'none',cursor:'pointer',minHeight:44 }}>
                <option value="">Select firm…</option>
                {PROP_FIRMS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          )}

          {/* Balance */}
          <div style={{ marginBottom:12 }}>
            <FieldLabel>Account Balance</FieldLabel>
            <TInput type="number" value={form.balance} onChange={set('balance')} placeholder="e.g. 10000" prefix="$" error={errors.balance} />
          </div>

          {/* Risk preview chips */}
          {balanceNum > 0 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, color:'rgba(200,192,168,.4)', marginBottom:8 }}>
                Risk per trade at <strong style={{ color: meta.color }}>${balanceNum.toLocaleString()}</strong>:
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6 }}>
                {[0.5,1,1.5,2,3].map(pct => {
                  const amt   = (balanceNum * pct / 100).toFixed(0);
                  const color = pct<=1?'var(--green)':pct<=2?'var(--gold)':'var(--red)';
                  const bg    = pct<=1?'rgba(46,204,138,.07)':pct<=2?'rgba(201,168,76,.07)':'rgba(217,79,79,.07)';
                  const bdr   = pct<=1?'rgba(46,204,138,.2)':pct<=2?'rgba(201,168,76,.2)':'rgba(217,79,79,.2)';
                  return (
                    <div key={pct} style={{ background:bg,border:`1px solid ${bdr}`,padding:'8px 4px',textAlign:'center',borderRadius:'var(--radius)' }}>
                      <div style={{ fontSize:9,color:'rgba(200,192,168,.5)',marginBottom:2,fontWeight:600 }}>{pct}%</div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:14,color,lineHeight:1 }}>${amt}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Risk rules */}
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <FieldLabel>Daily Loss Limit</FieldLabel>
              <TInput type="number" value={form.dailyPct} onChange={set('dailyPct')} placeholder="e.g. 3" suffix="%" />
              {balanceNum > 0 && form.dailyPct && (
                <div style={{ fontSize:11,color:'var(--red)',marginTop:3,fontWeight:600 }}>
                  = ${(balanceNum * parseFloat(form.dailyPct) / 100).toFixed(0)}/day
                </div>
              )}
            </div>
            <div>
              <FieldLabel>Weekly Target</FieldLabel>
              <TInput type="number" value={form.weekPct} onChange={set('weekPct')} placeholder="e.g. 10" suffix="%" />
              {balanceNum > 0 && form.weekPct && (
                <div style={{ fontSize:11,color:'var(--green)',marginTop:3,fontWeight:600 }}>
                  = ${(balanceNum * parseFloat(form.weekPct) / 100).toFixed(0)}/week
                </div>
              )}
            </div>
            <div>
              <FieldLabel>Max Trades / Day</FieldLabel>
              <TInput type="number" value={form.maxTrades} onChange={set('maxTrades')} placeholder="0 = off" />
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom:20 }}>
            <FieldLabel>Notes (optional)</FieldLabel>
            <textarea
              value={form.notes}
              onChange={set('notes')}
              placeholder="e.g. Phase 1 — 10% target, 5% max drawdown"
              rows={2}
              style={{
                width:'100%', padding:'10px 12px',
                background:'var(--dark3)', border:'1px solid rgba(255,255,255,.1)',
                color:'var(--white)', fontSize:13, outline:'none',
                borderRadius:'var(--radius)', resize:'vertical',
                fontFamily:"'Inter',sans-serif",
              }}
            />
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onCancel}
              style={{ flex:1,padding:'12px',background:'transparent',border:'1px solid rgba(255,255,255,.12)',color:'rgba(240,234,214,.6)',fontSize:13,fontWeight:600,cursor:'pointer',borderRadius:'var(--radius)',minHeight:44 }}>
              Cancel
            </button>
            <button onClick={handleSave}
              style={{ flex:2,padding:'12px',background:'linear-gradient(135deg,var(--gold),var(--gold-dim))',border:'none',color:'var(--black)',fontSize:13,fontWeight:700,cursor:'pointer',borderRadius:'var(--radius)',minHeight:44 }}>
              {isEdit ? '✓ Save Changes' : '+ Add Account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════ */
export default function SettingsPage({ session }) {
  const w        = useWindowWidth();
  const isMobile = w < 640;
  const isTablet = w >= 640 && w < 900;

  // ── Removed 'accounts' from default — first visible section is profile ──
  const [activeSection, setActiveSection] = useState('profile');

  /* ── Profile ── */
  const [name,     setName]     = useState(session?.name || '');
  const [level,    setLevel]    = useState('Level 2 Student');
  const [timezone, setTimezone] = useState('Africa/Kigali');

  /* ── Account (default/primary) ── */
  const [tradingAccounts,   setTradingAccounts]   = useState([]);
  const [activeAccountId,   setActiveAccountId]   = useState(null);
  const [showAccountForm,   setShowAccountForm]   = useState(false);
  const [editingAccount,    setEditingAccount]    = useState(null);

  /* ── Security ── */
  const [curPwd,   setCurPwd]   = useState('');
  const [newPwd,   setNewPwd]   = useState('');
  const [confPwd,  setConfPwd]  = useState('');
  const [pwdError, setPwdError] = useState('');

  /* ── Rules ── */
  const [rules,       setRules]       = useState([]);
  const [newRule,     setNewRule]     = useState('');
  const [rulesError,  setRulesError]  = useState('');
  const [rulesSaving, setRulesSaving] = useState(false);

  /* ── Saved baseline refs for dirty tracking ── */
  const savedProfileRef = useRef({ name: session?.name || '', level: 'Level 2 Student', timezone: 'Africa/Kigali' });

  // ── Load profile + goals + trading accounts from backend on mount ──────────
  useEffect(() => {
    api('/profile')
      .then(data => {
        const p = data.profile || {};
        const g = data.goals   || {};

        const pName     = data.user?.name       || session?.name || '';
        const pLevel    = p.level    || 'Level 2 Student';
        const pTimezone = p.timezone || 'Africa/Kigali';

        setName(pName);  setLevel(pLevel);  setTimezone(pTimezone);
        if (Array.isArray(p.tradingRules)) setRules(p.tradingRules);

        if (Array.isArray(p.tradingAccounts)) {
          setTradingAccounts(p.tradingAccounts);
          setActiveAccountId(p.activeAccountId || null);
          // Seed cache so TradeModal can self-resolve the active account on open
          // Key is user-scoped so two users on same browser never share data
          try {
            const uid = session?.id || '';
            sessionStorage.setItem(`kfx:accounts:${uid}`, JSON.stringify({
              tradingAccounts: p.tradingAccounts,
              activeAccountId: p.activeAccountId || null,
            }));
            sessionStorage.setItem('kfx:current_user', uid);
          } catch (_) {}
        }

        savedProfileRef.current = { name: pName, level: pLevel, timezone: pTimezone };
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Accept external navigation to 'accounts' section (e.g. from sidebar popover) ──
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section) setActiveSection(e.detail.section);
    };
    window.addEventListener('kfx:settings-navigate', handler);
    return () => window.removeEventListener('kfx:settings-navigate', handler);
  }, []);

  const profileDirty  = name !== savedProfileRef.current.name || level !== savedProfileRef.current.level || timezone !== savedProfileRef.current.timezone;
  const securityDirty = curPwd !== '' || newPwd !== '' || confPwd !== '';

  const dirtyMap = { profile: profileDirty, rules: false, security: securityDirty, privacy: false };

  /* ── UI ── */
  const [toast,              setToast]              = useState(null);
  const [confirm,            setConfirm]            = useState(null);
  const [navPendingConfirm,  setNavPendingConfirm]  = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const handleNavChange = useCallback((id) => {
    if (dirtyMap[activeSection]) {
      setNavPendingConfirm(id);
    } else {
      setActiveSection(id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, profileDirty, securityDirty]);

  /* ── Saves ── */
  const saveProfile = async () => {
    try {
      await api('/profile', {
        method: 'PUT',
        body: JSON.stringify({
          name, level, timezone,
          broker:       null,
          accType:      'Live',
          tradingRules: rules,
          tradingAccounts,
          activeAccountId,
        }),
      });
      savedProfileRef.current = { name, level, timezone };
      showToast('Profile saved');
    } catch (err) {
      showToast(err.message || 'Failed to save profile', 'error');
    }
  };


  /* ── Trading Account CRUD ── */
  const saveTradingAccounts = async (accounts, newActiveId) => {
    try {
      const current = await api('/profile');
      const p = current.profile || {};
      await api('/profile', {
        method: 'PUT',
        body: JSON.stringify({
          name:             current.user?.name || name,
          level:            p.level    || level,
          timezone:         p.timezone || timezone,
          broker:           p.broker   || null,
          accType:          p.accType  || 'Live',
          tradingRules:     p.tradingRules || rules,
          tradingAccounts:  accounts,
          activeAccountId:  newActiveId,
        }),
      });
      // Cache for TradeModal self-seeding — user-scoped key
      try {
        const uid = session?.id || '';
        sessionStorage.setItem(`kfx:accounts:${uid}`, JSON.stringify({
          tradingAccounts: accounts,
          activeAccountId: newActiveId,
        }));
        sessionStorage.setItem('kfx:current_user', uid);
      } catch (_) {}
    } catch (err) {
      showToast(err.message || 'Failed to save accounts', 'error');
    }
  };

  const handleAddAccount = async (acct) => {
    const updated = [...tradingAccounts, acct];
    const newActiveId = tradingAccounts.length === 0 ? acct.id : activeAccountId;
    setTradingAccounts(updated);
    if (tradingAccounts.length === 0) setActiveAccountId(acct.id);
    setShowAccountForm(false);
    await saveTradingAccounts(updated, newActiveId);
    window.dispatchEvent(new CustomEvent('kfx:accounts-updated', { detail: { tradingAccounts: updated, activeAccountId: newActiveId } }));
    showToast(`"${acct.name}" added`);
  };

  const handleEditAccount = async (acct) => {
    const updated = tradingAccounts.map(a => a.id === acct.id ? acct : a);
    setTradingAccounts(updated);
    setEditingAccount(null);
    await saveTradingAccounts(updated, activeAccountId);
    window.dispatchEvent(new CustomEvent('kfx:accounts-updated', { detail: { tradingAccounts: updated, activeAccountId } }));
    showToast(`"${acct.name}" updated`);
  };

  const handleDeleteAccount = (id) => {
    const acct = tradingAccounts.find(a => a.id === id);
    setConfirm({
      title: `Delete "${acct?.name}"?`,
      body: 'This account and its risk settings will be removed. Your trade journal data is not affected.',
      confirmLabel: 'Delete Account',
      danger: true,
      onConfirm: async () => {
        const updated = tradingAccounts.filter(a => a.id !== id);
        const newActiveId = activeAccountId === id ? (updated[0]?.id || null) : activeAccountId;
        setTradingAccounts(updated);
        setActiveAccountId(newActiveId);
        setConfirm(null);
        await saveTradingAccounts(updated, newActiveId);
        window.dispatchEvent(new CustomEvent('kfx:accounts-updated', { detail: { tradingAccounts: updated, activeAccountId: newActiveId } }));
        showToast('Account deleted');
      },
    });
  };

  const handleSetActiveAccount = async (id) => {
    setActiveAccountId(id);
    await saveTradingAccounts(tradingAccounts, id);
    window.dispatchEvent(new CustomEvent('kfx:accounts-updated', { detail: { tradingAccounts, activeAccountId: id } }));
    const acct = tradingAccounts.find(a => a.id === id);
    showToast(`Switched to "${acct?.name}"`);
  };

  const saveRules = async () => {
    setRulesError('');
    setRulesSaving(true);
    try {
      const current = await api('/profile');
      const p = current.profile || {};
      await api('/profile', {
        method: 'PUT',
        body: JSON.stringify({
          name:             current.user?.name || name,
          level:            p.level    || level,
          timezone:         p.timezone || timezone,
          broker:           p.broker   || null,
          accType:          p.accType  || 'Live',
          tradingRules:     rules,
          tradingAccounts:  p.tradingAccounts  || tradingAccounts,
          activeAccountId:  p.activeAccountId  || activeAccountId,
        }),
      });
      showToast('Rules saved');
    } catch (err) {
      setRulesError(err.message || 'Failed to save rules.');
    } finally {
      setRulesSaving(false);
    }
  };

  const changePwd = async () => {
    setPwdError('');
    if (!curPwd || !newPwd || !confPwd) return setPwdError('Fill in all fields.');
    if (newPwd.length < 8)              return setPwdError('New password must be at least 8 characters.');
    if (newPwd !== confPwd)             return setPwdError('Passwords do not match.');
    try {
      await api('/profile/password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword: curPwd, newPassword: newPwd }),
      });
      setCurPwd(''); setNewPwd(''); setConfPwd('');
      showToast('Password updated — please log in again on other devices');
    } catch (err) {
      setPwdError(err.message || 'Failed to update password.');
    }
  };

  const exportBackup = async () => {
    try {
      const [profileData, tradesData] = await Promise.all([
        api('/profile'),
        api('/trades?limit=200'),
      ]);
      const backup = {
        exportedAt: new Date().toISOString(),
        user:    profileData.user,
        profile: profileData.profile,
        goals:   profileData.goals,
        trades:  tradesData.trades || [],
        rules:   profileData.profile?.tradingRules || [],
        tradingAccounts: profileData.profile?.tradingAccounts || [],
      };
      const a = document.createElement('a');
      a.href = 'data:application/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(backup, null, 2));
      a.download = `kfx_backup_${new Date().toISOString().slice(0,10)}.json`;
      a.click();
    } catch (err) {
      showToast('Export failed: ' + (err.message || 'Unknown error'), 'error');
    }
  };

  const clearAllData = () => {
    setConfirm({
      title: 'Delete Account?',
      body: 'This permanently deletes your account and all trade data from the database. This cannot be undone.',
      confirmLabel: 'Yes, Delete Everything',
      danger: true,
      onConfirm: async () => {
        try {
          const password = window.prompt('Enter your password to confirm account deletion:');
          if (!password) { setConfirm(null); return; }
          await api('/profile', {
            method: 'DELETE',
            body: JSON.stringify({ password }),
          });
          localStorage.removeItem('kfx_rules');
          setConfirm(null);
          window.dispatchEvent(new Event('kfx:logout'));
        } catch (err) {
          setConfirm(null);
          showToast('Failed: ' + (err.message || 'Check your password'), 'error');
        }
      }
    });
  };

  const activeAcct = tradingAccounts.find(a => a.id === activeAccountId);
  const sectionHeaderStyle = {
    display:'flex', alignItems:'center', gap:10,
    marginBottom:20, paddingBottom:14,
    borderBottom:'1px solid rgba(255,255,255,.06)',
  };

  const grid2 = { display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:14, marginBottom:16 };
  const grid3 = { display:'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : '1fr 1fr 1fr', gap:12, marginBottom:16 };

  return (
    <>
      <style>{SETTINGS_STYLES}</style>
      <div className="settings-root settings-scroll" style={{ flex:1, overflowY:'auto', overflowX:'hidden' }}>

      {/* ── Page header ── */}
      <div style={{ padding: isMobile ? '20px 16px 0' : '28px 32px 0', maxWidth: isMobile ? '100%' : 900, width:'100%' }}>
        <div style={{ marginBottom: isMobile ? 16 : 24 }}>
          <div className="settings-section-title" style={{ fontSize: isMobile ? 17 : 20, fontWeight:800, color:'var(--white)', letterSpacing:'-.02em', marginBottom:4 }}>Settings</div>
          <div style={{ fontSize:12, color:'rgba(200,192,168,.4)' }}>Manage your profile, risk rules, and preferences.</div>
        </div>
      </div>

      {/* ── Mobile tab strip ── */}
      {isMobile && (
        <MobileNav active={activeSection} onChange={handleNavChange} dirty={dirtyMap} />
      )}

      {/* ── Two-col layout ── */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 0 : 28,
        alignItems: 'flex-start',
        padding: isMobile ? '0 0 80px' : '0 32px 40px',
        maxWidth: isMobile ? '100%' : 900,
        width: '100%',
      }}>

        {!isMobile && (
          <DesktopNav active={activeSection} onChange={handleNavChange} dirty={dirtyMap} />
        )}

        {/* ── Content panel ── */}
        <div style={{ flex:1, minWidth:0, animation:'fadeUp .25s ease', padding: isMobile ? '0 16px' : 0, width: isMobile ? '100%' : 'auto', boxSizing:'border-box' }}>

          {/* ════ PROFILE ════ */}
          {activeSection === 'profile' && (
            <div>
              <div style={sectionHeaderStyle}>
                <div style={{
                  width:isMobile?44:48, height:isMobile?44:48, flexShrink:0,
                  background:'linear-gradient(135deg,var(--gold),var(--gold-dim))',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:isMobile?14:16, fontWeight:900, color:'var(--black)',
                  borderRadius:'var(--radius)', position:'relative',
                  letterSpacing:'-.02em',
                }}>
                  {getInitials(name)}
                  <div style={{ position:'absolute',inset:-2,border:'1px solid rgba(201,168,76,.35)',borderRadius:'var(--radius)' }} />
                </div>
                <div>
                  <div style={{ fontSize:isMobile?13:15, fontWeight:700, color:'var(--white)' }}>{name || 'Your Name'}</div>
                  <div style={{ fontSize:11, color:'rgba(200,192,168,.45)', marginTop:2 }}>{session?.email || ''}</div>
                </div>
              </div>

              <div style={grid2}>
                <div>
                  <FieldLabel>Display Name</FieldLabel>
                  <TInput value={name} onChange={e => setName(e.target.value)} placeholder="Your name" autoFocus />
                </div>
                <div>
                  <FieldLabel>Email</FieldLabel>
                  <TInput value={session?.email || ''} readOnly />
                </div>
                <div>
                  <FieldLabel>Trading Level</FieldLabel>
                  <select value={level} onChange={e => setLevel(e.target.value)}
                    style={{ width:'100%',padding:'12px',background:'var(--dark3)',border:'1px solid rgba(255,255,255,.1)',color:'var(--white)',fontSize:14,outline:'none',borderRadius:'var(--radius)',appearance:'none',cursor:'pointer',minHeight:44 }}>
                    {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel>Timezone</FieldLabel>
                  <select value={timezone} onChange={e => setTimezone(e.target.value)}
                    style={{ width:'100%',padding:'12px',background:'var(--dark3)',border:'1px solid rgba(255,255,255,.1)',color:'var(--white)',fontSize:14,outline:'none',borderRadius:'var(--radius)',appearance:'none',cursor:'pointer',minHeight:44 }}>
                    {TIMEZONES.map(g => (
                      <optgroup key={g.group} label={g.group}>
                        {g.zones.map(z => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
                      </optgroup>
                    ))}
                  </select>
                  <div style={{ fontSize:11,color:'rgba(200,192,168,.35)',marginTop:4 }}>Aligns session times in your trade log.</div>
                </div>
              </div>

              <SaveButton dirty={profileDirty} onClick={saveProfile} label="Save Profile" fullWidth={isMobile} />
            </div>
          )}

          {/* ════ ACCOUNT & RISK ════ */}
          {/* ════ MY ACCOUNTS — reachable via sidebar or kfx:settings-navigate event ════ */}
          {activeSection === 'accounts' && (
            <div>
              <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:36, height:36, background:'rgba(212,168,67,.1)', border:'1px solid rgba(212,168,67,.2)', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>🗂️</div>
                    <div>
                      <div className="settings-section-title" style={{ fontSize:15, fontWeight:700, color:'var(--white)', letterSpacing:'-.01em' }}>My Accounts</div>
                      <div style={{ fontSize:11, color:'rgba(200,192,168,.4)', marginTop:1 }}>
                        {tradingAccounts.length === 0
                          ? 'No accounts yet — add one to get started'
                          : `${tradingAccounts.length} account${tradingAccounts.length !== 1 ? 's' : ''} · ${activeAcct ? `Active: ${activeAcct.name}` : 'None active'}`}
                      </div>
                    </div>
                  </div>
                  {tradingAccounts.length < 10 && (
                    <button
                      onClick={() => { setEditingAccount(null); setShowAccountForm(true); }}
                      style={{
                        padding:'9px 16px', fontSize:11, fontWeight:800,
                        background:'linear-gradient(135deg,var(--gold),var(--gold-dim))',
                        border:'none', color:'var(--black)', cursor:'pointer',
                        borderRadius:8, letterSpacing:'.05em', flexShrink:0,
                        display:'flex', alignItems:'center', gap:6, fontFamily:"'DM Sans',sans-serif",
                        transition:'all .15s', boxShadow:'0 4px 12px rgba(212,168,67,.2)',
                        width: isMobile ? '100%' : 'auto', justifyContent:'center',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.filter='brightness(1.08)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.filter='none'; e.currentTarget.style.transform='none'; }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      ADD ACCOUNT
                    </button>
                  )}
                </div>
              </div>

              {/* Empty state */}
              {tradingAccounts.length === 0 && (
                <div style={{
                  border:'1px dashed rgba(212,168,67,.18)', borderRadius:12,
                  padding: isMobile ? '32px 20px' : '48px 32px', textAlign:'center',
                  background:'rgba(212,168,67,.015)',
                  animation:'fadeUp .3s ease',
                }}>
                  <div style={{ fontSize:36, marginBottom:14 }}>📊</div>
                  <div className="settings-section-title" style={{ fontSize:15, fontWeight:700, color:'var(--white)', marginBottom:8 }}>Start tracking your accounts</div>
                  <div style={{ fontSize:12, color:'rgba(200,192,168,.4)', marginBottom:24, maxWidth:340, margin:'0 auto 24px', lineHeight:1.7 }}>
                    Add your live, demo, or prop firm accounts. Switch between them instantly to journal trades with the right balance and risk settings.
                  </div>
                  <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap', marginBottom:24 }}>
                    {Object.entries(ACCOUNT_TYPE_META).map(([type, meta]) => (
                      <div key={type} style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', background:meta.bg, border:`1px solid ${meta.border}`, borderRadius:20, fontSize:11, color:meta.color, fontWeight:600, fontFamily:"'DM Sans',sans-serif" }}>
                        <span>{meta.icon}</span> {type}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowAccountForm(true)}
                    style={{
                      padding:'11px 24px', fontSize:12, fontWeight:800,
                      background:'linear-gradient(135deg,var(--gold),var(--gold-dim))',
                      border:'none', color:'var(--black)', cursor:'pointer',
                      borderRadius:8, letterSpacing:'.05em', fontFamily:"'DM Sans',sans-serif",
                      transition:'all .15s', boxShadow:'0 4px 16px rgba(212,168,67,.25)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.filter='brightness(1.08)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.filter='none'; e.currentTarget.style.transform='none'; }}
                  >
                    + Add Your First Account
                  </button>
                </div>
              )}

              {/* Account cards grid */}
              {tradingAccounts.length > 0 && (
                <>
                  <div className="settings-accounts-grid" style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : '1fr 1fr', gap:12, marginBottom:16 }}>
                    {tradingAccounts.map(acct => (
                      <TradingAccountCard
                        key={acct.id}
                        acct={acct}
                        isActive={acct.id === activeAccountId}
                        onSetActive={handleSetActiveAccount}
                        onEdit={(a) => { setEditingAccount(a); setShowAccountForm(true); }}
                        onDelete={handleDeleteAccount}
                        isMobile={isMobile}
                      />
                    ))}
                  </div>
                  <div style={{ padding:'11px 14px', background:'rgba(91,143,232,.04)', border:'1px solid rgba(91,143,232,.12)', borderRadius:8, fontSize:11, color:'rgba(200,192,168,.5)', lineHeight:1.7, display:'flex', gap:8, alignItems:'flex-start' }}>
                    <span style={{ fontSize:13, flexShrink:0, marginTop:1 }}>💡</span>
                    <span><strong style={{ color:'var(--blue)' }}>Tip:</strong> The active account's balance and risk settings are used when you log trades. You can also switch accounts instantly from the bottom-left of the sidebar.</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ════ SECURITY ════ */}
          {activeSection === 'security' && (
            <div>
              <div style={sectionHeaderStyle}>
                <span style={{ fontSize:18 }}>🔒</span>
                <div style={{ fontSize:14,fontWeight:700,color:'var(--white)' }}>Security</div>
              </div>

              <div style={{ padding:'10px 14px',background:'rgba(91,143,232,.07)',border:'1px solid rgba(91,143,232,.2)',borderRadius:'var(--radius)',marginBottom:20,fontSize:12,color:'rgba(200,192,168,.6)',lineHeight:1.6 }}>
                🔐 Passwords are hashed with SHA-256 before storage. Plain-text passwords upgrade automatically on next change.
              </div>

              <div style={{ display:'flex',flexDirection:'column',gap:14,maxWidth: isMobile ? '100%' : 440,marginBottom:16 }}>
                <div>
                  <FieldLabel>Current Password</FieldLabel>
                  <TInput type="password" value={curPwd} onChange={e => setCurPwd(e.target.value)} placeholder="••••••••" />
                </div>
                <div style={{ display:'grid',gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',gap:14 }}>
                  <div>
                    <FieldLabel>New Password</FieldLabel>
                    <TInput type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="Min 8 chars" />
                  </div>
                  <div>
                    <FieldLabel>Confirm New Password</FieldLabel>
                    <TInput type="password" value={confPwd} onChange={e => setConfPwd(e.target.value)} placeholder="Repeat" />
                  </div>
                </div>
              </div>

              {pwdError && <div style={{ fontSize:12,color:'var(--red)',padding:'8px 12px',background:'rgba(224,85,85,.08)',border:'1px solid rgba(224,85,85,.2)',marginBottom:14,borderRadius:'var(--radius)' }}>{pwdError}</div>}

              <button onClick={changePwd}
                style={{
                  padding:'12px 24px', background:'transparent',
                  border:'1px solid rgba(255,255,255,.15)', color:'rgba(240,234,214,.7)',
                  fontSize:12, fontWeight:600, cursor:'pointer',
                  letterSpacing:'.03em', borderRadius:'var(--radius)', transition:'all .15s',
                  width: isMobile ? '100%' : 'auto', minHeight:44,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(212,168,67,.4)'; e.currentTarget.style.color='var(--gold)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.15)'; e.currentTarget.style.color='rgba(240,234,214,.7)'; }}>
                Change Password
              </button>
            </div>
          )}

          {/* ════ MY RULES ════ */}
          {activeSection === 'rules' && (
            <div>
              <div style={sectionHeaderStyle}>
                <span style={{ fontSize:18 }}>📋</span>
                <div style={{ fontSize:14,fontWeight:700,color:'var(--white)' }}>My Trading Rules</div>
              </div>
              <div style={{ fontSize:12,color:'rgba(200,192,168,.5)',lineHeight:1.7,marginBottom:20 }}>
                These rules appear as checkboxes in the Trade Modal (Execution tab) so you can track rule adherence on every trade. Max 10 rules.
              </div>

              <div style={{ display:'flex',flexDirection:'column',gap:8,marginBottom:16 }}>
                {rules.length === 0 && (
                  <div style={{ fontSize:12,color:'rgba(200,192,168,.3)',padding:'12px 0',fontStyle:'italic' }}>
                    No rules yet. Add your first rule below.
                  </div>
                )}
                {rules.map((rule, i) => (
                  <div key={i} style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'rgba(201,168,76,.04)',border:'1px solid rgba(201,168,76,.12)',borderRadius:'var(--radius)' }}>
                    <div style={{ width:20,height:20,background:'rgba(201,168,76,.1)',border:'1px solid rgba(201,168,76,.2)',borderRadius:2,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:10,color:'var(--gold)',fontWeight:700 }}>
                      {i+1}
                    </div>
                    <input
                      value={rule}
                      onChange={e => { const r=[...rules]; r[i]=e.target.value; setRules(r); }}
                      style={{ flex:1,background:'transparent',border:'none',outline:'none',color:'var(--white)',fontSize:13,fontFamily:"'Inter',sans-serif" }}
                    />
                    <button onClick={() => setRules(rules.filter((_,j)=>j!==i))}
                      style={{ background:'none',border:'none',color:'rgba(224,85,85,.5)',cursor:'pointer',fontSize:18,lineHeight:1,padding:'0 4px',transition:'color .15s' }}
                      onMouseEnter={e=>e.currentTarget.style.color='var(--red)'}
                      onMouseLeave={e=>e.currentTarget.style.color='rgba(224,85,85,.5)'}>
                      ×
                    </button>
                  </div>
                ))}
              </div>

              {rules.length < 10 && (
                <div style={{ display:'flex',gap:8,marginBottom:16 }}>
                  <input
                    value={newRule}
                    onChange={e=>setNewRule(e.target.value)}
                    onKeyDown={e=>{ if(e.key==='Enter' && newRule.trim()){ setRules([...rules, newRule.trim()]); setNewRule(''); } }}
                    placeholder="e.g. Only trade during London session"
                    style={{ flex:1,padding:'10px 14px',background:'var(--dark3)',border:'1px solid rgba(255,255,255,.09)',color:'var(--white)',fontSize:13,outline:'none',borderRadius:'var(--radius)' }}
                  />
                  <button
                    onClick={()=>{ if(newRule.trim()){ setRules([...rules, newRule.trim()]); setNewRule(''); } }}
                    style={{ padding:'10px 18px',background:'rgba(201,168,76,.12)',border:'1px solid rgba(201,168,76,.3)',color:'var(--gold)',fontSize:12,fontWeight:700,cursor:'pointer',borderRadius:'var(--radius)',whiteSpace:'nowrap' }}>
                    + Add
                  </button>
                </div>
              )}

              {rulesError && (
                <div style={{ padding:'10px 14px',background:'rgba(224,85,85,.08)',border:'1px solid rgba(224,85,85,.2)',borderRadius:'var(--radius)',fontSize:12,color:'var(--red)',marginBottom:12 }}>
                  ⚠ {rulesError}
                </div>
              )}

              <button onClick={saveRules} disabled={rulesSaving}
                style={{ padding:'12px 24px',background:rulesSaving?'rgba(201,168,76,.2)':'linear-gradient(135deg,var(--gold),var(--gold-dim))',border:'none',color:'var(--black)',fontSize:12,fontWeight:700,cursor:rulesSaving?'default':'pointer',borderRadius:'var(--radius)',transition:'all .15s' }}>
                {rulesSaving ? 'Saving…' : '💾 Save Rules'}
              </button>

              <div style={{ marginTop:16,padding:'12px 14px',background:'rgba(201,168,76,.04)',border:'1px solid rgba(201,168,76,.1)',borderRadius:'var(--radius)',fontSize:11,color:'rgba(200,192,168,.5)',lineHeight:1.7 }}>
                💡 <strong style={{color:'var(--gold)'}}>Tip:</strong> Keep rules specific and actionable. Examples: "Wait for session open", "Max 1% risk per trade", "No trades 30 min before news".
              </div>
            </div>
          )}

          {/* ════ DATA & PRIVACY ════ */}
          {activeSection === 'privacy' && (
            <div>
              <div style={sectionHeaderStyle}>
                <span style={{ fontSize:18 }}>🛡️</span>
                <div style={{ fontSize:14,fontWeight:700,color:'var(--white)' }}>Data & Privacy</div>
              </div>

              <div style={{ padding:'14px 16px',background:'rgba(255,255,255,.02)',border:'1px solid rgba(255,255,255,.06)',borderRadius:'var(--radius)',marginBottom:12,fontSize:12,color:'rgba(200,192,168,.5)',lineHeight:1.7 }}>
                All trade data and trading rules are stored securely in the KFX database, linked to your authenticated account.
              </div>
              <div style={{ padding:'14px 16px',background:'rgba(201,168,76,.04)',border:'1px solid rgba(201,168,76,.15)',borderRadius:'var(--radius)',marginBottom:24,fontSize:12,color:'rgba(200,192,168,.55)',lineHeight:1.7 }}>
                <strong style={{ color:'var(--gold)' }}>⚠ AI Coach disclosure:</strong> When you use the AI Coach, a summary of your trade analytics is sent to OpenRouter to generate insights. No personally identifying information or account credentials are included in that request.
              </div>

              <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', gap:10 }}>
                <button onClick={exportBackup}
                  style={{ padding:'12px 20px',background:'transparent',border:'1px solid rgba(212,168,67,.3)',color:'var(--gold)',fontSize:12,fontWeight:600,cursor:'pointer',borderRadius:'var(--radius)',transition:'all .15s',minHeight:44,flex: isMobile ? 'unset' : undefined }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,168,67,.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  📦 Export Backup (JSON)
                </button>
                <button onClick={clearAllData}
                  style={{ padding:'12px 20px',background:'transparent',border:'1px solid rgba(224,85,85,.3)',color:'var(--red)',fontSize:12,fontWeight:600,cursor:'pointer',borderRadius:'var(--radius)',transition:'all .15s',minHeight:44,flex: isMobile ? 'unset' : undefined }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(224,85,85,.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  🗑️ Clear All Data
                </button>
              </div>
            </div>
          )}

        </div>{/* end content panel */}
      </div>{/* end two-col */}

      {/* Account form modal */}
      {showAccountForm && (
        <AccountFormModal
          acct={editingAccount}
          onSave={editingAccount ? handleEditAccount : handleAddAccount}
          onCancel={() => { setShowAccountForm(false); setEditingAccount(null); }}
          isMobile={isMobile}
        />
      )}

      {/* Confirm modal */}
      {confirm && (
        <ConfirmModal
          title={confirm.title} body={confirm.body}
          confirmLabel={confirm.confirmLabel} danger={confirm.danger}
          onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)}
        />
      )}
      {navPendingConfirm && (
        <ConfirmModal
          title="Unsaved Changes"
          body="You have unsaved changes in this section. Leave without saving?"
          confirmLabel="Leave Without Saving"
          onConfirm={() => { setActiveSection(navPendingConfirm); setNavPendingConfirm(null); }}
          onCancel={() => setNavPendingConfirm(null)}
        />
      )}
      {toast && <Toast msg={toast.msg} type={toast.type} />}
      </div>
    </>
  );
}